import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
  Skeleton,
  Alert
} from '@mui/material';
import {
  GridView as GridViewIcon,
  ViewList as ListViewIcon,
  ViewModule as ViewModuleIcon
} from '@mui/icons-material';
import ProductCard from './ProductCard';

const ProductGrid = ({
  products = [],
  loading = false,
  error = null,
  onAddToCart,
  onToggleFavorite,
  onShare,
  onProductClick,
  onLoginRequired,
  itemsPerPage = 12,
  showPagination = true,
  showViewToggle = true,
  showSortOptions = true,
  defaultView = 'grid', // 'grid', 'list', 'compact'
  gridColumns = { xs: 1, sm: 2, md: 3, lg: 4 }
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState(defaultView);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'price', label: 'Price' },
    { value: 'rating', label: 'Rating' },
    { value: 'newest', label: 'Newest' },
    { value: 'popular', label: 'Popular' }
  ];

  // Sort products
  const sortedProducts = React.useMemo(() => {
    if (!products || products.length === 0) return [];

    return [...products].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'price':
          aValue = a.discountPrice || a.price;
          bValue = b.discountPrice || b.price;
          break;
        case 'rating':
          aValue = a.averageRating || 0;
          bValue = b.averageRating || 0;
          break;
        case 'newest':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'popular':
          aValue = a.orderCount || 0;
          bValue = b.orderCount || 0;
          break;
        case 'name':
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
      }

      if (sortOrder === 'desc') {
        return aValue < bValue ? 1 : -1;
      }
      return aValue > bValue ? 1 : -1;
    });
  }, [products, sortBy, sortOrder]);

  // Paginate products
  const paginatedProducts = React.useMemo(() => {
    if (!showPagination) return sortedProducts;
    
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedProducts.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedProducts, currentPage, itemsPerPage, showPagination]);

  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handleProductClick = (product) => {
    onProductClick?.(product);
  };

  // Reset to first page when products change
  useEffect(() => {
    setCurrentPage(1);
  }, [products]);

  const renderSkeletons = () => {
    const skeletonCount = viewMode === 'list' ? 5 : 8;
    return Array.from({ length: skeletonCount }, (_, index) => (
      <Grid 
        item 
        xs={viewMode === 'list' ? 12 : gridColumns.xs} 
        sm={viewMode === 'list' ? 12 : gridColumns.sm}
        md={viewMode === 'list' ? 12 : gridColumns.md}
        lg={viewMode === 'list' ? 12 : gridColumns.lg}
        key={index}
      >
        <ProductCard loading={true} />
      </Grid>
    ));
  };

  const getGridItemProps = () => {
    if (viewMode === 'list') {
      return { xs: 12 };
    }
    if (viewMode === 'compact') {
      return { 
        xs: gridColumns.xs, 
        sm: Math.min(gridColumns.sm * 2, 12), 
        md: Math.min(gridColumns.md * 2, 12),
        lg: Math.min(gridColumns.lg * 2, 12)
      };
    }
    return gridColumns;
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Controls */}
      {(showSortOptions || showViewToggle) && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }}>
          {/* Results count */}
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Loading...' : `${sortedProducts.length} products found`}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Sort options */}
            {showSortOptions && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={`${sortBy}-${sortOrder}`}
                  label="Sort by"
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                    setCurrentPage(1);
                  }}
                  displayEmpty
                >
                  {sortOptions.map(option => (
                    <React.Fragment key={option.value}>
                      <MenuItem value={`${option.value}-asc`}>
                        {option.label} (A-Z)
                      </MenuItem>
                      <MenuItem value={`${option.value}-desc`}>
                        {option.label} (Z-A)
                      </MenuItem>
                    </React.Fragment>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* View toggle */}
            {showViewToggle && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
              >
                <ToggleButton value="grid">
                  <GridViewIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ListViewIcon />
                </ToggleButton>
                <ToggleButton value="compact">
                  <ViewModuleIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
        </Box>
      )}

      {/* Products Grid */}
      <Grid container spacing={3}>
        {loading ? (
          renderSkeletons()
        ) : paginatedProducts.length === 0 ? (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No products found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search criteria or filters
              </Typography>
            </Box>
          </Grid>
        ) : (
          paginatedProducts.map((product, index) => (
            <Grid 
              item 
              {...getGridItemProps()}
              key={product.id || index}
            >
              <Box onClick={() => handleProductClick(product)} sx={{ cursor: 'pointer' }}>
                <ProductCard
                  product={product}
                  onAddToCart={onAddToCart}
                  onToggleFavorite={onToggleFavorite}
                  onShare={onShare}
                  onLoginRequired={onLoginRequired}
                  variant={viewMode}
                />
              </Box>
            </Grid>
          ))
        )}
      </Grid>

      {/* Pagination */}
      {showPagination && totalPages > 1 && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default ProductGrid;